import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import OpenInNewIcon from '@material-ui/icons/OpenInNew';
export const _frontmatter = {
  "title": "Reclamation Fund | How revenue works",
  "includeToc": true,
  "description": "Congress created the Reclamation Fund in 1902. The fund now receives disbursements from onshore natural resources revenue. The fund largely supports irrigation and hydropower projects overseen by the Bureau of Reclamation.",
  "tags": ["How revenue works", "onshore", "Bureau of Reclamation", "disbursements", "reclamation", "infrastructure", "water", "hydropower"]
};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><a parentName="p" {...{
        "href": "/sites/update-redirects/how-revenue-works/"
      }}>{`How revenue works /`}</a></p>

    <h1 {...{
      "id": "Reclamation-Fund",
      "style": {
        "position": "relative"
      }
    }}>{`Reclamation Fund`}</h1>
    <p>{`Congress created the Reclamation Fund in 1902. The fund now receives disbursements from onshore natural resources revenue. The fund largely supports irrigation and hydropower projects overseen by the `}<a parentName="p" {...{
        "href": "https://www.usbr.gov/"
      }}>{`Bureau of Reclamation `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{`.`}</p>
    <br />
    <p>{`The Reclamation Fund was created through `}<a parentName="p" {...{
        "href": "https://www.usbr.gov/lc/region/programs/contracts/Legislation.pdf"
      }}>{`The Reclamation Act of 1902 (PDF) `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{`. The fund was originally designed to support irrigation projects in the western United States. Its funding came primarily from the sale of federal land and timber. Over the years, Congress explored new revenue sources for the fund, and expanded the projects the fund was authorized to support.`}</p>
    <h2 {...{
      "id": "Funding-priorities",
      "style": {
        "position": "relative"
      }
    }}>{`Funding priorities`}</h2>
    <p>{`Disbursements from the Reclamation Fund go to the Bureau of Reclamation. The bureau uses these funds for construction, operation, and administration of irrigation and hydropower projects. `}<a parentName="p" {...{
        "href": "https://www.usbr.gov/main/about/mission.html"
      }}>{`The bureau has constructed more than 600 dams and reservoirs `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{` throughout its history.`}</p>
    <h2 {...{
      "id": "Authorization-and-funding",
      "style": {
        "position": "relative"
      }
    }}>{`Authorization and funding`}</h2>
    <p>{`The fund was originally designed to be revolving. That is, after initial funding by Congress, it was thought the fund would sustain itself through revenue from two main sources:`}</p>
    <ul>
      <li parentName="ul">{`federal land and timber sales`}</li>
      <li parentName="ul">{`payments from reclamation projects and water sales`}</li>
    </ul>
    <p>{`That funding model didn't pan out, and Congress later directed additional revenue to the fund, including 40% of onshore revenue from natural resource leasing on federal land, except in Alaska.`}</p>
    <p>{`Funding now comes from four major revenue sources:`}</p>
    <ul>
      <li parentName="ul">{`federal land and timber sales in western states`}</li>
      <li parentName="ul">{`repayments from reclamation projects and water contracts/sales`}</li>
      <li parentName="ul">{`reclamation hydropower projects`}</li>
      <li parentName="ul">{`natural resources revenue from federal land leases (except Alaska)`}</li>
    </ul>
    <p>{`Since the early 1990s, the fund's incoming revenue has, on average, significantly exceeded its outgoing appropriations. For every fiscal year between 1994 and 2018—except 2009—revenue exceeded appropriations by more than $100 million. As of the end of fiscal year 2018, the Reclamation Fund's balance was $16.6 billion.`}</p>
    <h2 {...{
      "id": "Distribution-of-funds",
      "style": {
        "position": "relative"
      }
    }}>{`Distribution of funds`}</h2>
    <p>{`Funds mostly go to three Bureau of Reclamation accounts for the purposes described above:`}</p>
    <ul>
      <li parentName="ul">{`Water and Related Resources`}</li>
      <li parentName="ul">{`Policy and Administrative`}</li>
      <li parentName="ul">{`Western Area Power Administration`}</li>
    </ul>
    <p>{`Between fiscal years 2003 and 2018, average appropriations from the Reclamation Fund have been $1.024 billion per year.`}</p>
    <h3 {...{
      "id": "Redirecting-funding",
      "style": {
        "position": "relative"
      }
    }}>{`Redirecting funding`}</h3>
    <p>{`Through legislation, Congress has redistributed some funds for a certain number of years. Title X of the `}<a parentName="p" {...{
        "href": "https://www.congress.gov/bill/111th-congress/house-bill/146"
      }}>{`Omnibus Public Lands Management Act of 2009 `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{` will redirect $120 million per year between fiscal years 2020 and 2034 toward Native American water rights settlement projects.`}</p>
    <br />
    <p>{`Congress has shown interest in redirecting additional funds, such as described in `}<a parentName="p" {...{
        "href": "https://www.congress.gov/bill/116th-congress/house-bill/2473"
      }}>{`H.R. 2473 `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{`.`}</p>
    <h2 {...{
      "id": "Sources-and-further-reading",
      "style": {
        "position": "relative"
      }
    }}>{`Sources and further reading`}</h2>
    <h3 {...{
      "id": "Congressional-Research-Service",
      "style": {
        "position": "relative"
      }
    }}>{`Congressional Research Service`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://fas.org/sgp/crs/misc/IF10042.pdf"
        }}>{`Congressional Research Service, The Reclamation Fund (May 21, 2019) (PDF) `}<OpenInNewIcon style={{
            fontSize: 20
          }} mdxType="OpenInNewIcon" /></a></li>
    </ul>
    <h3 {...{
      "id": "Legislation",
      "style": {
        "position": "relative"
      }
    }}>{`Legislation`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.govinfo.gov/app/details/USCODE-2011-title30/USCODE-2011-title30-chap3A-subchapI-sec191"
        }}>{`Disposition of moneys received (30 U.S.C. § 191) `}<OpenInNewIcon style={{
            fontSize: 20
          }} mdxType="OpenInNewIcon" /></a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.govinfo.gov/app/details/USCODE-2005-title43/USCODE-2005-title43-chap12-subchapII-sec391"
        }}>{`Establishment of “reclamation fund” (43 U.S.C. § 391) `}<OpenInNewIcon style={{
            fontSize: 20
          }} mdxType="OpenInNewIcon" /></a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.govinfo.gov/app/details/USCODE-2004-title43/USCODE-2004-title43-chap12-subchapII-sec392a"
        }}>{`Payment into reclamation fund of receipts from irrigation projects; transfer of power revenues to General Treasury after repayment of construction costs (43 U.S.C. § 392a) `}<OpenInNewIcon style={{
            fontSize: 20
          }} mdxType="OpenInNewIcon" /></a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.govinfo.gov/content/pkg/USCODE-2018-title30/html/USCODE-2018-title30-chap3A.htm"
        }}>{`Mineral Leasing Act of 1920 (30 U.S.C. § 181 et seq.) `}<OpenInNewIcon style={{
            fontSize: 20
          }} mdxType="OpenInNewIcon" /></a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      